/**
 * AiPromptsTab — AI tools > Prompts admin view (Phase X.3.7c C3).
 *
 * Read-only viewer of the live prompt-as-code templates (with version badges)
 * + a "request a change" form + this tenant's request queue. Prompts are NOT
 * editable here (Amanda's decided model): an editable store would let copy slip
 * past the em-dash / staff-name lints + review. Owner/admin only.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  aiPromptsApi,
  type PromptsResponse,
  type PromptChangeRequestsResponse,
  type PromptChangeStatus,
} from '@/lib/aiPrompts';
import { TOOL_LABELS, type ToolKey } from '@/lib/aiToolToggles';
import { formatRelative } from '@/lib/format';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';
import { SelectField } from '@/components/primitives/SelectField';

export interface AiPromptsTabProps {
  tenantSlug: string;
}

const STATUS_CHIP: Record<PromptChangeStatus, string> = {
  open: 'bg-warm-50 text-ink border-warm-300',
  applied: 'bg-sage-100 text-sage-700 border-sage-300',
  declined: 'bg-sand text-ink-soft border-sage-300',
};

function toolLabel(toolKey: string): string {
  return TOOL_LABELS[toolKey as ToolKey] ?? toolKey;
}

export function AiPromptsTab({ tenantSlug }: AiPromptsTabProps): ReactNode {
  const queryClient = useQueryClient();

  const promptsQuery = useQuery<PromptsResponse, Error>({
    queryKey: ['admin-review', 'prompts', tenantSlug],
    queryFn: ({ signal }) => aiPromptsApi.prompts(tenantSlug, signal),
    staleTime: 60_000,
  });

  const requestsQuery = useQuery<PromptChangeRequestsResponse, Error>({
    queryKey: ['admin-review', 'prompt-change-requests', tenantSlug],
    queryFn: ({ signal }) => aiPromptsApi.listRequests(tenantSlug, signal),
    staleTime: 30_000,
  });

  const prompts = promptsQuery.data?.prompts ?? [];
  const requests = requestsQuery.data?.rows ?? [];

  // Distinct tool_keys that actually have a live prompt (the form targets these).
  const toolOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const p of prompts) {
      if (!seen.has(p.tool_key)) {
        seen.add(p.tool_key);
        out.push(p.tool_key);
      }
    }
    return out;
  }, [prompts]);

  const [toolKey, setToolKey] = useState('');
  const [description, setDescription] = useState('');
  const [wording, setWording] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const effectiveToolKey = toolKey || toolOptions[0] || '';

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['admin-review', 'prompt-change-requests', tenantSlug],
    });
  };

  // The request is built at the press and travels as the variables.
  const createMutation = useMutation({
    mutationFn: (request: Parameters<typeof aiPromptsApi.createRequest>[1]) =>
      aiPromptsApi.createRequest(tenantSlug, request),
    onSuccess: async () => {
      setToast('Change sent');
      setDescription('');
      setWording('');
      setFormError(null);
      await invalidate();
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (args: { id: string; status: 'applied' | 'declined' }) =>
      aiPromptsApi.resolveRequest(tenantSlug, args.id, args.status),
    onSuccess: async (_d, args) => {
      setToast(`Request ${args.status}`);
      await invalidate();
    },
  });

  function submit(): void {
    if (!effectiveToolKey) {
      setFormError('Pick a tool.');
      return;
    }
    if (description.trim().length === 0) {
      setFormError('Describe the change you want.');
      return;
    }
    createMutation.mutate({
      tool_key: effectiveToolKey,
      description: description.trim(),
      suggested_wording: wording.trim() === '' ? null : wording.trim(),
    });
  }

  return (
    <div className="flex flex-col gap-7">
      {toast && (
        <div
          role="status"
          className="px-3 py-2 text-sm bg-sage-50 border border-sage-200 text-sage-800 rounded-md"
        >
          {toast}
        </div>
      )}

      {/* Viewer */}
      <section>
        <h3 className="font-display text-lg text-sage-800">Live prompts</h3>
        <p className="text-xs text-ink-soft mt-1 mb-2 max-w-prose">
          The instructions each AI tool runs on. To change one, describe the change below and the
          admin team makes the edit.
        </p>

        {promptsQuery.isLoading && <SkeletonRow />}
        {promptsQuery.isError && (
          <ErrorBanner error={promptsQuery.error} onRetry={() => promptsQuery.refetch()} what="the prompts" />
        )}
        {promptsQuery.isSuccess && (
          <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 divide-y divide-sage-800/[5%]">
            {prompts.map((p) => (
              <details key={p.prompt_key} className="px-3 py-2">
                <summary className="cursor-pointer text-sm text-ink flex items-center gap-2">
                  <span className="font-medium">{p.label}</span>
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-ink-soft bg-sand rounded-pill px-1.5 py-0.5">
                    v{p.version}
                  </span>
                </summary>
                <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-base bg-sage-50 border border-sage-800/[8%] p-3 text-xs text-ink-soft font-mono">
                  {p.template}
                </pre>
              </details>
            ))}
          </div>
        )}
      </section>

      {/* Request a change */}
      <section>
        <h3 className="text-sm font-semibold text-ink mb-2">Request a change</h3>
        <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-3 flex flex-col gap-3 max-w-2xl">
          <div>
            <label htmlFor="pcr-tool" className="block text-xs text-ink-soft mb-1">
              Tool
            </label>
            <SelectField size="default"
              id="pcr-tool"
              value={effectiveToolKey}
              onChange={(e) => setToolKey(e.target.value)}
              className="w-full"
            >
              {toolOptions.map((k) => (
                <option key={k} value={k}>
                  {toolLabel(k)}
                </option>
              ))}
            </SelectField>
          </div>
          <div>
            <label htmlFor="pcr-desc" className="block text-xs text-ink-soft mb-1">
              What should change?
            </label>
            <textarea
              id="pcr-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-base border border-sage-800/[12%] bg-paper px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              placeholder="Describe the change you'd like."
            />
          </div>
          <div>
            <label htmlFor="pcr-wording" className="block text-xs text-ink-soft mb-1">
              Suggested wording (optional)
            </label>
            <textarea
              id="pcr-wording"
              value={wording}
              onChange={(e) => setWording(e.target.value)}
              rows={2}
              className="w-full rounded-base border border-sage-800/[12%] bg-paper px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            />
          </div>
          {formError && <span className="text-xs text-pepper">{formError}</span>}
          <div>
            <button
              type="button"
              onClick={submit}
              disabled={createMutation.isPending}
              className="min-h-11 px-4 py-2 text-sm font-semibold text-cream bg-sage-700 rounded-md hover:bg-sage-800 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              {createMutation.isPending ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
          {createMutation.isError && <ErrorBanner error={createMutation.error} kind="write" />}
        </div>
      </section>

      {/* Request queue */}
      <section>
        <h3 className="text-sm font-semibold text-ink mb-2">Requests</h3>
        {requestsQuery.isLoading && <SkeletonRow />}
        {requestsQuery.isError && (
          <ErrorBanner error={requestsQuery.error} onRetry={() => requestsQuery.refetch()} what="the change requests" />
        )}
        {requestsQuery.isSuccess && requests.length === 0 && (
          <div className="px-3 py-6 text-center bg-sage-50 border border-sage-100 rounded-card text-sm text-ink">
            When you ask for a change, you'll see it here.
          </div>
        )}
        {requestsQuery.isSuccess && requests.length > 0 && (
          <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 divide-y divide-sage-800/[5%]">
            {requests.map((r) => (
              <div key={r.id} className="px-3 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-ink">{toolLabel(r.tool_key)}</span>
                  <span
                    className={`text-[10px] uppercase tracking-wide font-semibold rounded-pill px-1.5 py-0.5 border ${STATUS_CHIP[r.status]}`}
                  >
                    {r.status}
                  </span>
                  <span className="text-xs text-ink-soft">
                    {r.requested_by_display ?? 'someone'} · {formatRelative(r.created_at)}
                  </span>
                </div>
                <p className="text-sm text-ink-soft mt-1 whitespace-pre-wrap">{r.description}</p>
                {r.suggested_wording && (
                  <p className="text-xs text-ink-soft mt-1 italic whitespace-pre-wrap">
                    Suggested: {r.suggested_wording}
                  </p>
                )}
                {r.status === 'open' && (
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => resolveMutation.mutate({ id: r.id, status: 'applied' })}
                      disabled={resolveMutation.isPending}
                      className="min-h-9 px-3 py-1 text-xs font-medium text-sage-700 border border-sage-300 rounded-md hover:bg-sage-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                    >
                      Mark applied
                    </button>
                    <button
                      type="button"
                      onClick={() => resolveMutation.mutate({ id: r.id, status: 'declined' })}
                      disabled={resolveMutation.isPending}
                      className="min-h-9 px-3 py-1 text-xs font-medium text-ink-soft border border-sage-800/[12%] rounded-md hover:bg-sand disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                    >
                      Decline
                    </button>
                  </div>
                )}
                {r.status !== 'open' && r.resolved_by_display && (
                  <p className="text-xs text-ink-soft mt-1">
                    {r.status} by {r.resolved_by_display}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
